import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FileCheck2,
  Pencil,
  Play,
  Plus,
  Timer as TimerIcon,
  Trash2,
} from 'lucide-react';
import { Badge } from '@/components/badge';
import { Button } from '@/components/button';
import { Card } from '@/components/card';
import { Dialog } from '@/components/dialog';
import { EmptyState } from '@/components/empty-state';
import { Input } from '@/components/input';
import { Menu } from '@/components/menu';
import { Pager } from '@/components/pager';
import { Select } from '@/components/select';
import { Textarea } from '@/components/textarea';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import { PageHeader } from '@/components/page-header';
import { useClients } from '@/lib/clients';
import { useFormError } from '@/lib/form-error';
import {
  useCreateTimeEntry,
  useDeleteTimeEntry,
  useDraftInvoiceFromTime,
  useStartTimer,
  useTimeEntries,
  useUnbilledSummary,
  useUpdateTimeEntry,
} from '@/lib/time-entries';
import { dollars, formatDate as fmtDate, formatDuration, formatTime as fmtTime } from '@/lib/format';
import type { TimeEntryData } from '@/types/api';

function dateInputValue(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function TimeIndex() {
  const [clientFilter, setClientFilter] = useState('');
  const [billedFilter, setBilledFilter] = useState<'' | 'unbilled' | 'billed'>('');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const clients = useClients({ status: 'active' });
  const { data: paginated, isLoading } = useTimeEntries({
    client_id: clientFilter ? Number(clientFilter) : undefined,
    billed: billedFilter === '' ? undefined : billedFilter === 'billed',
    page,
  });
  const { data: unbilled } = useUnbilledSummary();
  const start = useStartTimer();

  const entries = paginated?.data ?? [];
  const meta = paginated?.meta;
  const editing = editingId != null ? entries.find((e) => e.id === editingId) ?? null : null;

  const totalUnbilledCents = useMemo(
    () => (unbilled ?? []).reduce((sum, row) => sum + row.total_cents, 0),
    [unbilled],
  );
  const totalUnbilledSeconds = useMemo(
    () => (unbilled ?? []).reduce((sum, row) => sum + row.total_seconds, 0),
    [unbilled],
  );

  return (
    <div className="space-y-5">
      <PageHeader
        icon={TimerIcon}
        title="Time"
        subtitle="Track work across clients and projects. Convert unbilled time into invoices."
        action={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" /> Add entry
          </Button>
        }
      />

      {/* Unbilled summary */}
      <Card className="overflow-hidden">
        <div className="flex items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-surface-muted)]/40 px-4 py-2.5">
          <h2 className="text-sm font-semibold">Unbilled time</h2>
          <div className="text-xs text-[var(--color-foreground-muted)]">
            <span className="font-medium text-[var(--color-foreground)]">{formatDuration(totalUnbilledSeconds)}</span>
            <span className="mx-1.5">·</span>
            <span className="font-medium text-[var(--color-foreground)]">{dollars(totalUnbilledCents)}</span>
          </div>
        </div>
        {!unbilled || unbilled.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-[var(--color-foreground-muted)]">
            No unbilled time. Nice.
          </div>
        ) : (
          <ul className="divide-y divide-[var(--color-border)]">
            {unbilled.map((row) => (
              <UnbilledRow key={row.client_id} row={row} />
            ))}
          </ul>
        )}
      </Card>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Select
          value={clientFilter}
          onChange={(e) => {
            setClientFilter(e.target.value);
            setPage(1);
          }}
          className="max-w-[200px]"
        >
          <option value="">All clients</option>
          {(clients.data?.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </Select>
        <Select
          value={billedFilter}
          onChange={(e) => {
            setBilledFilter(e.target.value as typeof billedFilter);
            setPage(1);
          }}
          className="max-w-[160px]"
        >
          <option value="">All time</option>
          <option value="unbilled">Unbilled</option>
          <option value="billed">Billed</option>
        </Select>
      </div>

      {/* Entries table */}
      {isLoading ? (
        <p className="text-sm text-[var(--color-foreground-muted)]">Loading…</p>
      ) : entries.length === 0 ? (
        <EmptyState
          icon={TimerIcon}
          title="No time entries yet"
          description="Start the timer from the top bar, or add one manually."
          action={
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" /> Add entry
            </Button>
          }
        />
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-[var(--color-border)] bg-[var(--color-surface-muted)]/50 text-left text-[var(--color-foreground-muted)]">
              <tr>
                <th className="px-4 py-2 font-medium">Date</th>
                <th className="px-4 py-2 font-medium">Client / Project</th>
                <th className="px-4 py-2 font-medium">Description</th>
                <th className="px-4 py-2 font-medium text-right">Duration</th>
                <th className="px-4 py-2 font-medium text-right">Amount</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <EntryRow
                  key={entry.id}
                  entry={entry}
                  onEdit={() => setEditingId(entry.id)}
                  onResume={() =>
                    start.mutate({
                      client_id: entry.client_id,
                      project_id: entry.project_id ?? undefined,
                      card_id: entry.card_id ?? undefined,
                      description: entry.description ?? undefined,
                      billable: entry.billable,
                    })
                  }
                />
              ))}
            </tbody>
          </table>
          {meta ? (
            <Pager
              page={meta.current_page}
              lastPage={meta.last_page}
              total={meta.total}
              from={meta.from}
              to={meta.to}
              onChange={setPage}
            />
          ) : null}
        </Card>
      )}

      <CreateEntryDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      <EditEntryDialog
        entry={editing}
        onClose={() => setEditingId(null)}
      />
    </div>
  );
}

function UnbilledRow({
  row,
}: {
  row: {
    client_id: number;
    client_name: string | null;
    entry_count: number;
    total_seconds: number;
    total_cents: number;
  };
}) {
  const draft = useDraftInvoiceFromTime();
  const navigate = useNavigate();
  const toast = useToast();
  return (
    <li className="flex items-center justify-between px-4 py-2.5 text-sm">
      <Link to={`/clients/${row.client_id}`} className="font-medium hover:underline">
        {row.client_name ?? `Client #${row.client_id}`}
      </Link>
      <div className="flex items-center gap-4 text-[var(--color-foreground-muted)]">
        <span>{row.entry_count} entries</span>
        <span className="tabular-nums">{formatDuration(row.total_seconds)}</span>
        <span className="font-medium tabular-nums text-[var(--color-foreground)]">
          {dollars(row.total_cents)}
        </span>
        <Button
          variant="secondary"
          size="sm"
          disabled={draft.isPending}
          onClick={async () => {
            try {
              await draft.mutateAsync({ client_id: row.client_id });
              toast.success('Draft invoice created');
              navigate(`/clients/${row.client_id}?tab=approvables`);
            } catch (err) {
              const message =
                (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
                'Could not draft invoice.';
              toast.error('Could not draft invoice', message);
            }
          }}
        >
          <FileCheck2 className="size-3.5" /> Bill this time
        </Button>
      </div>
    </li>
  );
}

// Hoisted because the delete handler is called from inside <Menu> items.
function EntryRow({
  entry,
  onEdit,
  onResume,
}: {
  entry: TimeEntryData;
  onEdit: () => void;
  onResume: () => void;
}) {
  const del = useDeleteTimeEntry();
  const confirm = useConfirm();
  const toast = useToast();

  return (
    <tr className="border-b border-[var(--color-border)] last:border-0 hover:bg-[var(--color-surface-muted)]/50">
      <td className="whitespace-nowrap px-4 py-2.5">
        <div className="font-medium">{fmtDate(entry.started_at)}</div>
        <div className="text-xs text-[var(--color-foreground-subtle)]">
          {fmtTime(entry.started_at)}
          {entry.ended_at ? ` – ${fmtTime(entry.ended_at)}` : ' – running'}
        </div>
      </td>
      <td className="px-4 py-2.5">
        <Link to={`/clients/${entry.client_id}`} className="font-medium hover:underline">
          {entry.client_name ?? `Client #${entry.client_id}`}
        </Link>
        {entry.project_id ? (
          <div className="text-xs text-[var(--color-foreground-subtle)]">
            <Link to={`/projects/${entry.project_id}`} className="hover:underline">
              {entry.project_name ?? `Project #${entry.project_id}`}
            </Link>
          </div>
        ) : null}
      </td>
      <td className="max-w-[280px] px-4 py-2.5">
        {entry.description ? (
          <span className="line-clamp-2 text-[var(--color-foreground-muted)]">{entry.description}</span>
        ) : (
          <span className="text-xs text-[var(--color-foreground-subtle)]">—</span>
        )}
      </td>
      <td className="whitespace-nowrap px-4 py-2.5 text-right font-medium tabular-nums">
        {formatDuration(entry.duration_seconds)}
      </td>
      <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums text-[var(--color-foreground-muted)]">
        {dollars(entry.amount_cents)}
      </td>
      <td className="px-4 py-2.5">
        {entry.is_running ? (
          <Badge tone="success">Running</Badge>
        ) : entry.billed ? (
          <Badge tone="info">Billed</Badge>
        ) : entry.billable ? (
          <Badge tone="warning">Unbilled</Badge>
        ) : (
          <Badge tone="neutral">Non-billable</Badge>
        )}
      </td>
      <td className="whitespace-nowrap px-4 py-2.5 text-right">
        <Menu
          items={[
            { key: 'edit', label: 'Edit', icon: Pencil, onSelect: onEdit, disabled: entry.is_running },
            { key: 'resume', label: 'Start a new timer like this', icon: Play, onSelect: onResume },
            {
              key: 'delete',
              label: 'Delete',
              icon: Trash2,
              danger: true,
              disabled: entry.billed,
              onSelect: async () => {
                const ok = await confirm({
                  title: 'Delete this time entry?',
                  body: `${formatDuration(entry.duration_seconds)} for ${entry.client_name ?? 'untitled client'} on ${fmtDate(entry.started_at)}.`,
                  confirmLabel: 'Delete entry',
                  tone: 'danger',
                });
                if (ok) {
                  await del.mutateAsync(entry.id);
                  toast.success('Time entry deleted');
                }
              },
            },
          ]}
        />
      </td>
    </tr>
  );
}

function CreateEntryDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const clients = useClients({ status: 'active' });
  const create = useCreateTimeEntry();
  const { error, wrap } = useFormError();

  const [clientId, setClientId] = useState('');
  const [startedAt, setStartedAt] = useState('');
  const [endedAt, setEndedAt] = useState('');
  const [description, setDescription] = useState('');
  const [billable, setBillable] = useState(true);

  function reset() {
    setClientId('');
    setStartedAt('');
    setEndedAt('');
    setDescription('');
    setBillable(true);
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const ok = await wrap(() =>
      create.mutateAsync({
        client_id: Number(clientId),
        started_at: new Date(startedAt).toISOString(),
        ended_at: new Date(endedAt).toISOString(),
        description: description || undefined,
        billable,
      }),
    );
    if (ok) {
      reset();
      onClose();
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title="Add time entry">
      <form onSubmit={submit} className="space-y-4">
        <label className="block space-y-1 text-sm">
          <span className="font-medium text-[var(--color-foreground-muted)]">Client</span>
          <Select value={clientId} onChange={(e) => setClientId(e.target.value)} required>
            <option value="">Pick a client…</option>
            {(clients.data?.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </Select>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-[var(--color-foreground-muted)]">Started at</span>
            <Input
              type="datetime-local"
              value={startedAt}
              onChange={(e) => setStartedAt(e.target.value)}
              required
            />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-[var(--color-foreground-muted)]">Ended at</span>
            <Input
              type="datetime-local"
              value={endedAt}
              onChange={(e) => setEndedAt(e.target.value)}
              required
            />
          </label>
        </div>
        <label className="block space-y-1 text-sm">
          <span className="font-medium text-[var(--color-foreground-muted)]">Description</span>
          <Textarea
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What did you work on?"
          />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={billable}
            onChange={(e) => setBillable(e.target.checked)}
            className="size-4"
          />
          <span>Billable</span>
        </label>
        {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Saving…' : 'Save entry'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function EditEntryDialog({
  entry,
  onClose,
}: {
  entry: TimeEntryData | null;
  onClose: () => void;
}) {
  const update = useUpdateTimeEntry();
  const { error, wrap } = useFormError();
  const [startedAt, setStartedAt] = useState('');
  const [endedAt, setEndedAt] = useState('');
  const [description, setDescription] = useState('');
  const [billable, setBillable] = useState(true);
  const [seeded, setSeeded] = useState<number | null>(null);

  // Seed local form state when the dialog opens with a new entry.
  if (entry && seeded !== entry.id) {
    setStartedAt(dateInputValue(entry.started_at));
    setEndedAt(entry.ended_at ? dateInputValue(entry.ended_at) : '');
    setDescription(entry.description ?? '');
    setBillable(entry.billable);
    setSeeded(entry.id);
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!entry) return;
    const ok = await wrap(() =>
      update.mutateAsync({
        id: entry.id,
        patch: {
          started_at: new Date(startedAt).toISOString(),
          ended_at: endedAt ? new Date(endedAt).toISOString() : null,
          description: description || null,
          billable,
        },
      }),
    );
    if (ok) {
      setSeeded(null);
      onClose();
    }
  }

  return (
    <Dialog
      open={entry != null}
      onClose={() => {
        setSeeded(null);
        onClose();
      }}
      title="Edit time entry"
    >
      {entry ? (
        <form onSubmit={submit} className="space-y-4">
          <div className="rounded-md bg-[var(--color-surface-muted)] px-3 py-2 text-xs text-[var(--color-foreground-muted)]">
            <div className="font-medium text-[var(--color-foreground)]">
              {entry.client_name ?? `Client #${entry.client_id}`}
            </div>
            {entry.project_name ? <div>{entry.project_name}</div> : null}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block space-y-1 text-sm">
              <span className="font-medium text-[var(--color-foreground-muted)]">Started at</span>
              <Input
                type="datetime-local"
                value={startedAt}
                onChange={(e) => setStartedAt(e.target.value)}
                required
              />
            </label>
            <label className="block space-y-1 text-sm">
              <span className="font-medium text-[var(--color-foreground-muted)]">Ended at</span>
              <Input
                type="datetime-local"
                value={endedAt}
                onChange={(e) => setEndedAt(e.target.value)}
                required
              />
            </label>
          </div>
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-[var(--color-foreground-muted)]">Description</span>
            <Textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={billable}
              onChange={(e) => setBillable(e.target.checked)}
              className="size-4"
            />
            <span>Billable</span>
          </label>
          {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </form>
      ) : null}
    </Dialog>
  );
}
